import React from 'react';
import {
  AppBar, // Not directly used as a top bar, but good for context if header was more complex
  Toolbar,
  Typography,
  Container,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  Badge,
  Paper,
  Stack,
  createTheme,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';

// Example friend data (replace with actual data and image URLs)
const friends = [
  { id: 1, name: 'Jaxen Compton', avatar: 'https://i.imgur.com/avatar1.jpg', mutualFriends: 5, isOnline: true },
  { id: 2, name: 'Jalen Everett', avatar: 'https://i.imgur.com/avatar2.jpg', mutualFriends: 5, isOnline: true },
  { id: 3, name: 'Briley Carson', avatar: 'https://i.imgur.com/avatar3.jpg', mutualFriends: 5, isOnline: false },
  { id: 4, name: 'Samuel O\'neill', avatar: 'https://i.imgur.com/avatar4.jpg', mutualFriends: 5, isOnline: true },
  { id: 5, name: 'Travis Mack', avatar: 'https://i.imgur.com/avatar5.jpg', mutualFriends: 5, isOnline: false },
  { id: 6, name: 'Jaylah Day', avatar: 'https://i.imgur.com/avatar6.jpg', mutualFriends: 5, isOnline: true },
  { id: 7, name: 'Larry Buckner', avatar: 'https://i.imgur.com/avatar7.jpg', mutualFriends: 5, isOnline: false },
  { id: 8, name: 'Brayden Peters', avatar: 'https://i.imgur.com/avatar8.jpg', mutualFriends: 5, isOnline: true },
  { id: 9, name: 'Isiah Parker', avatar: 'https://i.imgur.com/avatar9.jpg', mutualFriends: 5, isOnline: false },
  { id: 10, name: 'Halle Guthrie', avatar: 'https://i.imgur.com/avatar10.jpg', mutualFriends: 5, isOnline: true },
  { id: 11, name: 'Alan Cline', avatar: 'https://i.imgur.com/avatar11.jpg', mutualFriends: 5, isOnline: true },
  { id: 12, name: 'Paislee Jones', avatar: 'https://i.imgur.com/avatar12.jpg', mutualFriends: 5, isOnline: false },
];

// Fallback for image URLs if not provided:
// const defaultAvatar = "https://via.placeholder.com/150/0000FF/FFFFFF?text=FN";

// Custom MUI theme for consistent styling
const theme = createTheme({
  palette: {
    primary: {
      main: '#28a745', // Green for online indicator and scrollbar
    },
    secondary: {
      main: '#f8f9fa', // Light background for the page
    },
    background: {
      default: '#f8f9fa',
      paper: '#fff', // White background for the friend list Paper
    },
    text: {
      primary: '#333',
      secondary: '#6c757d',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h5: {
      fontWeight: 600,
      fontSize: '1.8rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    subtitle1: { // For friend names
      fontWeight: 500,
      fontSize: '1.05rem',
    },
    body2: { // For mutual friends count
      fontSize: '0.9rem',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px', // Rounded corners for the main friend list card
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)', // Subtle shadow
          // Custom scrollbar styles (might need -webkit- for Chrome/Safari)
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#28a745', // Green scrollbar thumb
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#218838', // Darker green on hover
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '25px', // Rounded search bar
            backgroundColor: '#f5f5f5', // Light background
            '& fieldset': {
              borderColor: 'transparent', // No border by default
            },
            '&:hover fieldset': {
              borderColor: '#e0e0e0', // Subtle border on hover
            },
            '&.Mui-focused fieldset': {
              borderColor: '#28a745', // Green border on focus
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#6c757d', // Default icon button color
        },
      },
    },
    MuiListItemText: {
        styleOverrides: {
            primary: {
                fontWeight: 600, // Make names bolder
                lineHeight: 1.2,
            },
            secondary: {
                color: '#6c757d', // Grey for mutual friends
            }
        }
    },
    MuiAvatar: {
        styleOverrides: {
            root: {
                width: 56, // Larger avatars as seen in the image
                height: 56,
            }
        }
    }
  },
});

function FollowersList() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
        <Container maxWidth="sm"> {/* Adjusted maxWidth for the list layout */}
          {/* Header */}
          <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3, ml: { xs: 0, sm: 2 } }}>
            My Friends
          </Typography>

          {/* Search and Filter */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 3, px: { xs: 0, sm: 2 } }}
          >
            <TextField
              variant="outlined"
              placeholder="Search for Notification"
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton aria-label="filter">
              <FilterListIcon />
            </IconButton>
          </Stack>

          {/* Friend List */}
          <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'auto', maxHeight: '70vh' }}> {/* Added maxHeight and overflow */}
            <List disablePadding>
              {friends.map((friend, index) => (
                <React.Fragment key={friend.id}>
                  <ListItem
                    button // Makes the entire item clickable with ripple effect
                    sx={{
                      py: 1.5,
                      px: { xs: 2, sm: 3 }, // Responsive padding
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)', // Subtle hover effect
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        color="success" // Green dot
                        invisible={!friend.isOnline} // Hide if not online
                        sx={{
                          '& .MuiBadge-badge': {
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            border: '2px solid white', // White border around dot
                            backgroundColor: theme.palette.primary.main,
                          },
                        }}
                      >
                        <Avatar alt={friend.name} src={friend.avatar} />
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" noWrap>
                          {friend.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {friend.mutualFriends} Mutual Friends
                        </Typography>
                      }
                      sx={{ ml: 1 }} // Margin left for text next to avatar
                    />
                  </ListItem>
                  {index < friends.length - 1 && <Divider component="li" sx={{ ml: 8, mr: 2 }} />} {/* Indented divider */}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default FollowersList;